import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  UserIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  ArrowRightIcon,
  CheckIcon,
  GlobeAltIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

interface OnboardingData {
  // Student fields
  university?: string;
  major?: string;
  graduationYear?: number;
  skills?: string[];
  bio?: string;
  interests?: string[];
  careerGoals?: string[];
  skillsToLearn?: string[];
  
  // Employer fields
  companyName?: string;
  companySize?: string;
  industry?: string;
  website?: string;
  location?: string;
  companyDescription?: string;
}

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, updateProfile, loading } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({});
  const [skillInput, setSkillInput] = useState('');
  const [interestInput, setInterestInput] = useState('');
  const [careerGoalInput, setCareerGoalInput] = useState('');
  const [skillToLearnInput, setSkillToLearnInput] = useState('');

  // Smart suggestions based on major
  const getSuggestionsForMajor = (major: string) => {
    const majorLower = major.toLowerCase();
    const suggestions: { interests: string[], careerGoals: string[], skillsToLearn: string[] } = {
      interests: [],
      careerGoals: [],
      skillsToLearn: []
    };

    if (majorLower.includes('computer science') || majorLower.includes('cs')) {
      suggestions.interests = ['Programming', 'Technology', 'Problem Solving', 'Innovation'];
      suggestions.careerGoals = ['Software Engineer', 'Full Stack Developer', 'Backend Developer', 'Frontend Developer'];
      suggestions.skillsToLearn = ['JavaScript', 'Python', 'React', 'Node.js', 'SQL'];
    } else if (majorLower.includes('data science') || majorLower.includes('data')) {
      suggestions.interests = ['Data Analysis', 'Machine Learning', 'Statistics', 'Research'];
      suggestions.careerGoals = ['Data Scientist', 'Data Analyst', 'Machine Learning Engineer', 'Research Scientist'];
      suggestions.skillsToLearn = ['Python', 'R', 'SQL', 'Machine Learning', 'Statistics'];
    } else if (majorLower.includes('marketing') || majorLower.includes('business')) {
      suggestions.interests = ['Marketing', 'Strategy', 'Communication', 'Analytics'];
      suggestions.careerGoals = ['Marketing Manager', 'Digital Marketing Specialist', 'Product Manager', 'Business Analyst'];
      suggestions.skillsToLearn = ['Google Analytics', 'SEO', 'Content Marketing', 'Social Media Marketing'];
    } else if (majorLower.includes('design') || majorLower.includes('graphic')) {
      suggestions.interests = ['Design', 'Creativity', 'Visual Arts', 'User Experience'];
      suggestions.careerGoals = ['UX Designer', 'UI Designer', 'Graphic Designer', 'Product Designer'];
      suggestions.skillsToLearn = ['Figma', 'Adobe Creative Suite', 'Prototyping', 'User Research'];
    } else if (majorLower.includes('engineering')) {
      suggestions.interests = ['Engineering', 'Problem Solving', 'Innovation', 'Technology'];
      suggestions.careerGoals = ['Software Engineer', 'Systems Engineer', 'Product Engineer', 'Technical Lead'];
      suggestions.skillsToLearn = ['Programming', 'System Design', 'Project Management', 'Technical Writing'];
    }

    return suggestions;
  };

  const isStudent = userProfile?.userType === 'student';
  const totalSteps = isStudent ? 4 : 3;

  useEffect(() => {
    if (!userProfile) {
      navigate('/login');
      return;
    }
  }, [userProfile, navigate]);

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSkipOnboarding = () => {
    navigate('/dashboard');
    toast.success('Welcome to Workazon! You can complete your profile later.');
  };

  const handleComplete = async () => {
    try {
      // Update basic profile data
      await updateProfile(data);
      
      // Save interests if it's a student and has interests data
      if (isStudent && (data.interests || data.careerGoals || data.skillsToLearn)) {
        const interestsData = {
          interests: data.interests || [],
          careerGoals: data.careerGoals || [],
          skillsToLearn: data.skillsToLearn || []
        };
        
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profiles/interests`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ interests: interestsData })
        });

        if (!response.ok) {
          throw new Error('Failed to save interests');
        }
      }
      
      toast.success('Profile completed successfully!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Onboarding completion error:', error);
      toast.error('Failed to update profile. Please try again.');
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && (!data.skills || !data.skills.includes(skillInput.trim()))) {
      // Split by comma and add multiple skills
      const skills = skillInput.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0);
      setData(prev => ({
        ...prev,
        skills: [...(prev.skills || []), ...skills]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill: string) => {
    setData(prev => ({
      ...prev,
      skills: prev.skills?.filter(s => s !== skill) || []
    }));
  };

  const addInterest = () => {
    if (interestInput.trim() && (!data.interests || !data.interests.includes(interestInput.trim()))) {
      // Split by comma and add multiple interests
      const interests = interestInput.split(',').map(interest => interest.trim()).filter(interest => interest.length > 0);
      setData(prev => ({
        ...prev,
        interests: [...(prev.interests || []), ...interests]
      }));
      setInterestInput('');
    }
  };

  const removeInterest = (interest: string) => {
    setData(prev => ({
      ...prev,
      interests: prev.interests?.filter(i => i !== interest) || []
    }));
  };

  const addCareerGoal = () => {
    if (careerGoalInput.trim() && (!data.careerGoals || !data.careerGoals.includes(careerGoalInput.trim()))) {
      // Split by comma and add multiple career goals
      const goals = careerGoalInput.split(',').map(goal => goal.trim()).filter(goal => goal.length > 0);
      setData(prev => ({
        ...prev,
        careerGoals: [...(prev.careerGoals || []), ...goals]
      }));
      setCareerGoalInput('');
    }
  };

  const removeCareerGoal = (goal: string) => {
    setData(prev => ({
      ...prev,
      careerGoals: prev.careerGoals?.filter(g => g !== goal) || []
    }));
  };

  const addSkillToLearn = () => {
    if (skillToLearnInput.trim() && (!data.skillsToLearn || !data.skillsToLearn.includes(skillToLearnInput.trim()))) {
      // Split by comma and add multiple skills to learn
      const skills = skillToLearnInput.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0);
      setData(prev => ({
        ...prev,
        skillsToLearn: [...(prev.skillsToLearn || []), ...skills]
      }));
      setSkillToLearnInput('');
    }
  };

  const removeSkillToLearn = (skill: string) => {
    setData(prev => ({
      ...prev,
      skillsToLearn: prev.skillsToLearn?.filter(s => s !== skill) || []
    }));
  };

  const handleInputChange = (field: keyof OnboardingData, value: any) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const renderStudentOnboarding = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <AcademicCapIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                Tell us about your education
              </h2>
              <p className="mt-2 text-gray-600">
                Help employers understand your academic background
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  University/College
                </label>
                <input
                  type="text"
                  placeholder="e.g., Stanford University"
                  value={data.university || ''}
                  onChange={(e) => handleInputChange('university', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Major/Field of Study
                </label>
                <input
                  type="text"
                  placeholder="e.g., Computer Science"
                  value={data.major || ''}
                  onChange={(e) => handleInputChange('major', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Graduation Year
                </label>
                <select
                  value={data.graduationYear || ''}
                  onChange={(e) => handleInputChange('graduationYear', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select year</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i - 5).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <UserIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                What are your skills?
              </h2>
              <p className="mt-2 text-gray-600">
                Add skills that showcase your expertise
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Skills
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="e.g., JavaScript, React, Python (separate with commas)"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {data.skills && data.skills.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Skills
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {data.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="ml-2 text-indigo-600 hover:text-indigo-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <HeartIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                What interests you?
              </h2>
              <p className="mt-2 text-gray-600">
                Tell us about your interests and career goals to get personalized recommendations
              </p>
              {data.major && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 Based on your major in <strong>{data.major}</strong>, we've prepared some suggestions below!
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Interests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Interests
                </label>
                <div className="flex space-x-2 mb-3">
                  <input
                    type="text"
                    placeholder="e.g., Technology, Design, Marketing (separate with commas)"
                    value={interestInput}
                    onChange={(e) => setInterestInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addInterest()}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addInterest}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {data.interests && data.interests.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {data.interests.map((interest, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {interest}
                        <button
                          type="button"
                          onClick={() => removeInterest(interest)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Interest Suggestions */}
                {data.major && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 mb-2">Quick suggestions for {data.major}:</p>
                    <div className="flex flex-wrap gap-2">
                      {getSuggestionsForMajor(data.major).interests.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            if (!data.interests?.includes(suggestion)) {
                              setData(prev => ({
                                ...prev,
                                interests: [...(prev.interests || []), suggestion]
                              }));
                            }
                          }}
                          className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors"
                        >
                          + {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Career Goals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Career Goals
                </label>
                <div className="flex space-x-2 mb-3">
                  <input
                    type="text"
                    placeholder="e.g., Software Engineer, Data Scientist (separate with commas)"
                    value={careerGoalInput}
                    onChange={(e) => setCareerGoalInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCareerGoal()}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addCareerGoal}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {data.careerGoals && data.careerGoals.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {data.careerGoals.map((goal, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                      >
                        {goal}
                        <button
                          type="button"
                          onClick={() => removeCareerGoal(goal)}
                          className="ml-2 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Career Goal Suggestions */}
                {data.major && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 mb-2">Popular career paths for {data.major}:</p>
                    <div className="flex flex-wrap gap-2">
                      {getSuggestionsForMajor(data.major).careerGoals.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            if (!data.careerGoals?.includes(suggestion)) {
                              setData(prev => ({
                                ...prev,
                                careerGoals: [...(prev.careerGoals || []), suggestion]
                              }));
                            }
                          }}
                          className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-green-100 hover:text-green-700 transition-colors"
                        >
                          + {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Skills to Learn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills You Want to Learn
                </label>
                <div className="flex space-x-2 mb-3">
                  <input
                    type="text"
                    placeholder="e.g., Machine Learning, Cloud Computing (separate with commas)"
                    value={skillToLearnInput}
                    onChange={(e) => setSkillToLearnInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addSkillToLearn()}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addSkillToLearn}
                    className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {data.skillsToLearn && data.skillsToLearn.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {data.skillsToLearn.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkillToLearn(skill)}
                          className="ml-2 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Skills to Learn Suggestions */}
                {data.major && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 mb-2">Recommended skills for {data.major}:</p>
                    <div className="flex flex-wrap gap-2">
                      {getSuggestionsForMajor(data.major).skillsToLearn.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            if (!data.skillsToLearn?.includes(suggestion)) {
                              setData(prev => ({
                                ...prev,
                                skillsToLearn: [...(prev.skillsToLearn || []), suggestion]
                              }));
                            }
                          }}
                          className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-purple-100 hover:text-purple-700 transition-colors"
                        >
                          + {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <UserIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                Tell us about yourself
              </h2>
              <p className="mt-2 text-gray-600">
                Write a brief bio to help employers get to know you
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bio (optional)
              </label>
              <textarea
                rows={4}
                placeholder="Tell employers about your passions, experiences, and what you're looking for..."
                value={data.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderEmployerOnboarding = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <BuildingOfficeIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                Tell us about your company
              </h2>
              <p className="mt-2 text-gray-600">
                Help candidates learn about your organization
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Acme Corporation"
                  value={data.companyName || ''}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={data.industry || ''}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select industry</option>
                  <option value="technology">Technology</option>
                  <option value="finance">Finance</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="consulting">Consulting</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Size
                </label>
                <select
                  value={data.companySize || ''}
                  onChange={(e) => handleInputChange('companySize', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501-1000">501-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <MapPinIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                Where are you located?
              </h2>
              <p className="mt-2 text-gray-600">
                Help candidates understand your company's presence
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  placeholder="e.g., San Francisco, CA"
                  value={data.location || ''}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website (optional)
                </label>
                <input
                  type="url"
                  placeholder="https://www.yourcompany.com"
                  value={data.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <BriefcaseIcon className="mx-auto h-16 w-16 text-indigo-600" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                Describe your company
              </h2>
              <p className="mt-2 text-gray-600">
                Tell candidates what makes your company special
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Description (optional)
              </label>
              <textarea
                rows={4}
                placeholder="Describe your company's mission, culture, and what you're looking for in candidates..."
                value={data.companyDescription || ''}
                onChange={(e) => handleInputChange('companyDescription', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white">
            Welcome to Workazon, {userProfile.firstName}!
          </h1>
          <p className="mt-2 text-indigo-200">
            Let's set up your profile to get you started
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>

        <div className="text-center text-indigo-200 text-sm">
          Step {step} of {totalSteps}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          {isStudent ? renderStudentOnboarding() : renderEmployerOnboarding()}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div className="flex space-x-4">
              {step > 1 && (
                <button
                  onClick={handlePrevious}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
              )}
              
              <button
                onClick={handleSkipOnboarding}
                className="px-6 py-3 text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip for now
              </button>
            </div>

            {step < totalSteps ? (
              <button
                onClick={handleNext}
                className="flex items-center px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Continue
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="flex items-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Complete Setup
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-indigo-200">
            You can always update your profile later in settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
